import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { t } from 'i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { ChipListCell } from 'core/elements';
import { formatDate } from 'core/utils';
import { VendorQuoteImportDeliveredVia, VendorQuoteImportOutcome } from 'dashboard/enums';
import { IVendorQuoteImportAttempt } from 'dashboard/types';

import { AnomaliesCell } from './AnomaliesCell';
import { DeliveredViaCell } from './DeliveredViaCell';
import { VendorQuoteLogColId } from './enums';
import { FileNameCell } from './FileNameCell';
import { ImportedManufacturerCell } from './ImportedManufacturerCell';
import { ImportIdCell } from './ImportIdCell';
import { ResultCell } from './ResultCell';
import { VendorQuoteIdCell } from './VendorQuoteIdCell';
import {
  formatDurationSeconds,
  getAnomalyLabels,
  getCompanyLabel,
  getImportErrorLabel,
  getManufacturerStatusWarning,
} from '../utils';

const getOutcomeLabel = (outcome?: VendorQuoteImportOutcome) => {
  switch (outcome) {
    case VendorQuoteImportOutcome.SUCCESS:
      return t('labelSuccess');
    case VendorQuoteImportOutcome.FAILED:
      return t('labelFailed');
    case VendorQuoteImportOutcome.IN_PROGRESS:
      return t('labelInProgress');
    default:
      return null;
  }
};

// Leaf rows only: on group rows (no data) every column in this table renders
// blank, so the placeholder would read as a value there.
const formatWithPlaceholder = ({ value, data }: ValueFormatterParams) =>
  data ? value || DEFAULT_PLACEHOLDER : value;

// The cell shows a warning icon plus this reason when there is no manufacturer
// to display; the formatter drives exports and the clipboard, so it spells the
// same reason out rather than exporting a bare placeholder.
const getImportedManufacturerLabel = (attempt: IVendorQuoteImportAttempt) =>
  attempt.importedManufacturer ||
  getManufacturerStatusWarning(attempt)?.label ||
  DEFAULT_PLACEHOLDER;

const formatImportedManufacturer = ({ value, data }: ValueFormatterParams) =>
  data ? getImportedManufacturerLabel(data) : value;

// The shared cell renderer falls back to the placeholder for any falsy value, so
// a real 0 renders as "-" — "unknown" instead of "none", and on these columns the
// zero is the whole point. Format it explicitly; only a null means unknown.
const formatCount = ({ value, data }: ValueFormatterParams) => {
  if (!data) {
    return value;
  }

  return null == value ? DEFAULT_PLACEHOLDER : String(value);
};

const getDeliveredViaLabel = (deliveredVia?: VendorQuoteImportDeliveredVia) => {
  switch (deliveredVia) {
    case VendorQuoteImportDeliveredVia.POLL:
      return t('labelPoll');
    case VendorQuoteImportDeliveredVia.PUSH:
      return t('labelPush');
    default:
      return null;
  }
};

// DeliveredViaCell renders a third state the enum has no member for, so the
// formatter has to know about it too — it is what feeds Excel, CSV and the
// clipboard, and the filter reads the same label. Without this the cell says
// "Other instance" while the export writes the blank the cell exists to avoid.
const getDeliveredViaValue = (attempt: IVendorQuoteImportAttempt) =>
  getDeliveredViaLabel(attempt.deliveredVia) ||
  (attempt.processedElsewhere ? t('labelProcessedElsewhere') : null);

// One tooltip for the whole Result cell. The grid runs tooltipMouseTrack with no
// delay, so ResultCell cannot add its own without the two stacking.
const getResultTooltip = (attempt: IVendorQuoteImportAttempt) =>
  [getImportErrorLabel(attempt), attempt.failedPartCount ? t('tooltipPartsImportedRatio') : null]
    .filter(Boolean)
    .join(' — ');

export const columnDef = (): ColDef[] => [
  {
    colId: VendorQuoteLogColId.COMPANY,
    headerName: t('headerCompany'),
    // No `field`: the value is built from two fields, so the getter is the only
    // source — that keeps sorting, grouping, filtering, the tooltip and the
    // export all reading the same string the cell shows.
    valueGetter: ({ data }) => data && getCompanyLabel(data),
    filter: 'agTextColumnFilter',
    enableRowGroup: true,
    minWidth: 180,
    tooltipValueGetter: ({ data }) => data && getCompanyLabel(data),
  },
  {
    colId: VendorQuoteLogColId.USER,
    headerName: t('headerUser'),
    field: 'userName',
    filter: 'agTextColumnFilter',
    enableRowGroup: true,
    minWidth: 160,
  },
  {
    colId: VendorQuoteLogColId.STARTED_AT,
    headerName: t('headerDateTime'),
    field: 'startedAt',
    filter: 'agDateColumnFilter',
    enableRowGroup: false,
    minWidth: 170,
    valueFormatter: ({ value }) => value && formatDate(value, true),
  },
  {
    colId: VendorQuoteLogColId.OUTCOME,
    headerName: t('headerResult'),
    field: 'outcome',
    filter: 'agTextColumnFilter',
    enableRowGroup: false,
    width: 120,
    valueFormatter: ({ value }) => value && getOutcomeLabel(value),
    filterValueGetter: ({ data }) => data && getOutcomeLabel(data.outcome),
    tooltipValueGetter: ({ data }) => data && getResultTooltip(data),
    cellRenderer: ResultCell,
  },
  {
    colId: VendorQuoteLogColId.IMPORT_ID,
    headerName: t('headerImportId'),
    field: 'importId',
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
    enableRowGroup: false,
    width: 110,
    cellRenderer: ImportIdCell,
  },
  {
    colId: VendorQuoteLogColId.DOCUMENT_ID,
    headerName: t('headerDocumentId'),
    field: 'documentId',
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
    enableRowGroup: false,
    width: 120,
  },
  {
    colId: VendorQuoteLogColId.VENDOR_QUOTE_ID,
    headerName: t('headerVendorQuoteId'),
    field: 'quoteId',
    filter: 'agNumberColumnFilter',
    enableRowGroup: true,
    width: 140,
    cellRenderer: VendorQuoteIdCell,
  },
  {
    colId: VendorQuoteLogColId.CUSTOMER_QUOTE_IDS,
    headerName: t('headerCustomerQuoteIds'),
    field: 'customerQuoteIds',
    filter: 'agTextColumnFilter',
    // The value is an array, so sorting and grouping are meaningless; filtering
    // and the tooltip run off the joined string instead.
    sortable: false,
    enableRowGroup: false,
    minWidth: 160,
    cellRenderer: ChipListCell,
    valueGetter: ({ data }) => data?.customerQuoteIds?.map(String) ?? [],
    filterValueGetter: ({ data }) => data?.customerQuoteIds?.join(', ') ?? '',
    tooltipValueGetter: ({ data }) => data?.customerQuoteIds?.join(', ') ?? '',
  },
  {
    colId: VendorQuoteLogColId.FILE_NAME,
    headerName: t('headerFileName'),
    field: 'fileName',
    filter: 'agTextColumnFilter',
    enableRowGroup: true,
    minWidth: 200,
    // The cell links to the upload; sorting, filtering, grouping and export all
    // keep reading the plain `fileName` field, so the link changes nothing but
    // what the reader clicks.
    cellRenderer: FileNameCell,
    headerTooltip: t('tooltipHeaderFileName'),
  },
  {
    colId: VendorQuoteLogColId.IMPORTED_MANUFACTURER,
    headerName: t('headerImportedManufacturer'),
    field: 'importedManufacturer',
    filter: 'agTextColumnFilter',
    enableRowGroup: true,
    minWidth: 190,
    valueFormatter: formatImportedManufacturer,
    // Without this the filter and quick search read the raw field, which is null
    // on exactly the rows whose cell and export both read "Import not
    // persisted" — searching for the phrase the export shows would match none.
    filterValueGetter: ({ data }) => data && getImportedManufacturerLabel(data),
    cellRenderer: ImportedManufacturerCell,
  },
  {
    colId: VendorQuoteLogColId.PARTFINITI_MANUFACTURER,
    headerName: t('headerPartfinitiManufacturer'),
    field: 'partfinitiManufacturer',
    filter: 'agTextColumnFilter',
    enableRowGroup: true,
    minWidth: 170,
    valueFormatter: formatWithPlaceholder,
  },
  {
    colId: VendorQuoteLogColId.PART_COUNT,
    headerName: t('headerParts'),
    field: 'partCount',
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
    enableRowGroup: false,
    width: 100,
    valueFormatter: formatCount,
    headerTooltip: t('tooltipHeaderPartsFromPayload'),
  },
  {
    colId: VendorQuoteLogColId.IMPORTED_PART_COUNT,
    headerName: t('headerImportedParts'),
    field: 'importedPartCount',
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
    enableRowGroup: false,
    valueFormatter: formatCount,
    width: 110,
    headerTooltip: t('tooltipHeaderImportedParts'),
  },
  {
    colId: VendorQuoteLogColId.DISCOUNT_COUNT,
    headerName: t('headerDiscountItems'),
    field: 'discountCount',
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
    enableRowGroup: false,
    width: 130,
    valueFormatter: formatCount,
  },
  {
    colId: VendorQuoteLogColId.DURATION,
    headerName: t('headerDuration'),
    field: 'durationSeconds',
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
    enableRowGroup: false,
    width: 110,
    valueFormatter: ({ value }) => formatDurationSeconds(value),
  },
  {
    colId: VendorQuoteLogColId.POLL_COUNT,
    headerName: t('headerPolls'),
    field: 'pollCount',
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
    enableRowGroup: false,
    width: 100,
  },
  {
    colId: VendorQuoteLogColId.DELIVERED_VIA,
    headerName: t('headerDeliveredVia'),
    field: 'deliveredVia',
    filter: 'agTextColumnFilter',
    enableRowGroup: false,
    width: 130,
    valueFormatter: ({ data }) => data && getDeliveredViaValue(data),
    filterValueGetter: ({ data }) => data && getDeliveredViaValue(data),
    cellRenderer: DeliveredViaCell,
  },
  {
    colId: VendorQuoteLogColId.ANOMALIES,
    headerName: t('headerAnomalies'),
    field: 'anomalies',
    filter: 'agTextColumnFilter',
    // The value is an array of server flags, so sorting is meaningless; filtering
    // runs off the joined badge labels instead.
    sortable: false,
    enableRowGroup: false,
    minWidth: 170,
    cellRenderer: AnomaliesCell,
    // The valueFormatter feeds Excel export and clipboard; the cell itself renders chips.
    // Read off `data` (not `value`) so the per-flag counts reach the badge labels.
    valueFormatter: ({ data }) => data && getAnomalyLabels(data),
    filterValueGetter: ({ data }) => data && getAnomalyLabels(data),
  },
  {
    colId: VendorQuoteLogColId.ERROR,
    headerName: t('headerError'),
    field: 'error',
    filter: 'agTextColumnFilter',
    enableRowGroup: false,
    minWidth: 220,
    headerTooltip: t('tooltipHeaderError'),
    valueGetter: ({ data }) => data && getImportErrorLabel(data),
  },
];
